import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useLocation } from 'react-router-dom';

import { Container, Grid, Paper, Typography } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { useGetRateCodesByCodes } from '~/hooks/apis/cruises/useGetRateCodesByCodes';
import { useGetRateGroups } from '~/hooks/apis/cruises/useGetRateGroups';
import { useGetRatesByGroup } from '~/hooks/apis/cruises/useGetRatesByGroup';
import useAppNavigation from '~/hooks/useAppNavigation';

import { paramToArray } from '~/utils/url';

import RateByGroupTabs from './components/RateByGroupTabs';
import RateCodeSelect from './components/RateCodeSelect';
import RateGroupList from './components/RateGroupList';
import RecentlyCreatedRateCodeSelect from './components/RecentlyCreatedRateCodeSelect';
import { Vendor } from './components/VendorTab';
import VendorTabSection from './components/VendorTabSection';

function RateGroupingManagementPage() {
  const location = useLocation();
  const { replace } = useAppNavigation();
  const [selectedVendorId, setSelectedVendorId] = useState<string | null>(null);
  const [manualSelectionVersion, setManualSelectionVersion] = useState(0);
  const rateCodes = useMemo(
    () => Array.from(new Set(paramToArray(new URLSearchParams(location.search).get('rateCodes') ?? undefined) ?? [])),
    [location.search],
  );
  const rateCodesQuery = useGetRateCodesByCodes(rateCodes);
  const selectedVendor = useMemo<Vendor | null>(() => {
    if (!rateCodes.length) {
      return null;
    }
    const rates = rateCodesQuery.data?.result ?? [];
    const vendor = rates.find((rate) => rate.vendor.id === selectedVendorId)?.vendor ?? rates[0]?.vendor;
    return vendor
      ? {
          id: vendor.id,
          name: vendor.name,
          rates: rates.filter((rate) => rate.vendor.id === vendor.id).map((rate) => ({ code: rate.code })),
        }
      : null;
  }, [rateCodes.length, rateCodesQuery.data, selectedVendorId]);
  const rateGroupsQuery = useGetRateGroups(selectedVendor?.id);
  const ratesByGroupQuery = useGetRatesByGroup(
    selectedVendor?.id,
    selectedVendor?.rates.map((rate) => rate.code) ?? [],
  );
  const handleSelectVendor = useCallback((vendor: Vendor) => setSelectedVendorId(vendor.id), []);
  const handleRateCodesChange = useCallback(
    (codes: Array<string>) => {
      const params = new URLSearchParams(location.search);
      const normalizedCodes = Array.from(new Set(codes.map((code) => code.trim()).filter(Boolean)));
      if (normalizedCodes.length) {
        params.set('rateCodes', normalizedCodes.join(','));
      } else {
        params.delete('rateCodes');
      }
      replace({ ...location, search: params.toString() }, location.state);
    },
    [replace, location],
  );
  const handleManualRateCodesChange = useCallback(
    (codes: Array<string>) => {
      setManualSelectionVersion((version) => version + 1);
      handleRateCodesChange(codes);
    },
    [handleRateCodesChange],
  );

  useEffect(() => {
    if (!rateCodes.length || rateCodesQuery.isSuccess) {
      setSelectedVendorId(selectedVendor?.id ?? null);
    }
  }, [rateCodes.length, rateCodesQuery.isSuccess, selectedVendor?.id]);

  return (
    <Container maxWidth="xl">
      <PageHeader title="Rate Group Assignment Assistant" backButton="/cruises/rate-grouping" />
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography fontWeight={700} mb={1}>
          Select rate codes to get started.
        </Typography>
        <RateCodeSelect value={rateCodes} onChange={handleManualRateCodesChange} />
        <Typography fontWeight={700} mb={1}>
          Or select recently created rates:
        </Typography>
        <RecentlyCreatedRateCodeSelect onChange={handleRateCodesChange} resetKey={manualSelectionVersion} />
      </Paper>

      {rateCodes.length > 0 && (
        <>
          <Typography variant="h6" mb={2}>
            Rates by vendors
          </Typography>

          <VendorTabSection
            rateCodesQuery={rateCodesQuery}
            selectedVendor={selectedVendor}
            onSelectVendor={handleSelectVendor}
          />

          {selectedVendor && (
            <Grid container spacing={2} mt={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <RateGroupList rateGroupsQuery={rateGroupsQuery} vendorId={selectedVendor?.id} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <RateByGroupTabs
                  ratesByGroupQuery={ratesByGroupQuery}
                  rateGroups={rateGroupsQuery.data ?? []}
                  shouldFetchRateDetails={rateGroupsQuery.isSuccess && (rateGroupsQuery.data?.length ?? 0) > 0}
                  vendorId={selectedVendor?.id}
                />
              </Grid>
            </Grid>
          )}
        </>
      )}
    </Container>
  );
}

export default RateGroupingManagementPage;
